import React, { useEffect, useState, useRef } from 'react';
import { makeStyles } from "@mui/styles";
import { styled } from '@mui/material/styles';
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, InputAdornment, IconButton, Step, Stepper, StepContent, StepLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Button, FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { X } from 'lucide-react';

import FlagstoneTheme from '../../../../Config/flagstoneTheme';
import { searchTypes } from '../../../../Config/constants';
import { dataService } from '../../../../Services/data/data.service';

export default function BagweigherUserDialog({ dialogOpen, closeDialog, editing, selectedUser, allUsers, updateBagweigherUserLocally, addBagweigherUserLocally }) {

    const classes = useStyles();
    const { t } = useTranslation();

    // User data + errors
    const [id, setId] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [badgenumber, setBadgenumber] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [receiveMail, setReceiveMail] = useState(true);
    
    const [isScanning, setIsScanning] = useState(false);

    const [idError, setIdError] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);

    // barcode button
    const [hovered, setHovered] = useState(false);

    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && selectedUser) {
            setId(selectedUser.Id);
            setName(selectedUser.Name);
            setEmail(selectedUser.Email);
            setIsAdmin(selectedUser.IsAdmin);
            setIsActive(selectedUser.IsActive);
            setReceiveMail(selectedUser.ReceiveMail);
        }
    }, [selectedUser, editing]);

    const handleScanBarcode = () => {
        setBadgenumber("");
        setIsScanning(true);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    const processBarcode = (e) => {
        if (e.key === "Enter") {
            setHovered(false);
            inputRef.current.blur();
            setBadgenumber(e.target.value);
            setIsScanning(false);
            inputRef.current.value = "";
        }
    }

    const cancel = () => {
        closeDialog();
    }

    const submitUser = () => {
        if (editing) {
            updateUser();
        } else {
            createUser();
        }
    }

    const createUser = () => {
        let user = {
            Id: id,
            Name: name,
            Email: email,
            IsAdmin: isAdmin,
            Badgenumber: badgenumber,
            IsActive: isActive,
            ReceiveMail: receiveMail
        }

        if (validateUser(user)) {
            dataService.createBagweigherUser(user).then((response) => {
                if (response.ok) {
                    addBagweigherUserLocally(JSON.parse(JSON.parse(response.data.Messages[0]).Data).user);
                    closeDialog();
                    toast.success(t("User created successfully"), { position: "top-center" });
                } else {
                    toast.error(t("Error creating user: ") + response.data, { position: "top-center" });
                }
            })
        }
    }

    const updateUser = () => {
        let tempBadgenumber = "";

        if (badgenumber == "") {
            if (selectedUser.Badgenumber != "") {
                tempBadgenumber = selectedUser.Badgenumber
            }
        } else {
            tempBadgenumber = badgenumber;
        }

        let user = {
            Id: id,
            Name: name,
            Email: email,
            IsAdmin: isAdmin,
            Badgenumber: tempBadgenumber,
            IsActive: isActive,
            ReceiveMail: receiveMail
        }

        if (validateUser(user)) {
            dataService.updateBagweigherUser(user).then((response) => {
                if (response.ok) {
                    updateBagweigherUserLocally(user);
                    closeDialog();
                    toast.success(t("User updated successfully"), { position: "top-center" });
                } else {
                    toast.error(t("Error updating user: ") + response.data, { position: "top-center" });
                }
            })
        }
    }

    const resetErrors = () => {
        setIdError(false);
        setNameError(false);
        setEmailError(false);
    }

    const checkIdAvailability = (id) => {
        let user = allUsers.find(u => u.Id === id);
        if (user) {
            return true;
        } else {
            return false;
        }
    }

    const validateUser = (user) => {
        let errors = 0;

        resetErrors();

        if (!user.Name) {
            setNameError(t("Name is required"));
            errors++;
        }
        // if (!user.Email) {
        //     setEmailError(t("Email is required"));
        //     errors++;
        // }
        // Check if email is valid
        if (user.Email) {
            let emailPattern = new RegExp(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/);
            if (!emailPattern.test(user.Email)) {
                setEmailError(t("Email is not valid"));
                errors++;
            }
        }
        if (!user.Id) {
            setIdError(t("Id is required"));
            errors++;
        }

        // Check if id is already in use when creating a user or check if id is already in use by another user when editing

        if (editing) {
            if (selectedUser.Id !== user.Id) {

                if (checkIdAvailability(user.id)) {
                    setIdError(t("Id is already in use"));
                    errors++;
                }
            }
        } else {
            if (checkIdAvailability(user.Id)) {
                setIdError(t("Id is already in use"));
                errors++;
            }
        }

        if (errors > 0) {
            return false;
        } else {
            return true;

        }
    }

    const FlagstoneSwitch = styled((props) => (
        <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
    ))(({ theme }) => ({
        width: 38,
        height: 20,
        padding: 0,
        '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: 1,
            transitionDuration: '300ms',
            '&.Mui-checked': {
                transform: 'translateX(18px)',
                color: FlagstoneTheme.palette.primary.main,
                '& + .MuiSwitch-track': {
                    backgroundColor: '#EDF2FF',
                    opacity: 1,
                    border: 0,
                    ...theme.applyStyles('dark', {
                        backgroundColor: '#2ECA45',
                    }),
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                    opacity: 0.5,
                },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: FlagstoneTheme.palette.primary.main,
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
                color: theme.palette.grey[100],
                ...theme.applyStyles('dark', {
                    color: theme.palette.grey[600],
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.7,
                ...theme.applyStyles('dark', {
                    opacity: 0.3,
                }),
            },
        },
        '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 18,
            height: 18,
        },
        '& .MuiSwitch-track': {
            borderRadius: 20 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transition: theme.transitions.create(['background-color'], {
                duration: 500,
            }),
            ...theme.applyStyles('dark', {
                backgroundColor: '#39393D',
            }),
        },
    }));

    return (

        <Dialog className={classes.dialog}
            open={dialogOpen}
            onClose={closeDialog}

            classes={{ paper: classes.dialogPaper }}

            aria-labelledby="draggable-dialog-title"
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {editing ? t("Edit user - Bagweigher") : t("Add user - Bagweigher")}
                </DialogTitle>
                <button onClick={() => closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <div className={classes.dialog__form}>
                        <div className={classes.form__badgeContainer}>
                            {editing ?
                                selectedUser.Badgenumber == "" && badgenumber == "" ?
                                    <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${isScanning ? classes.scanning : ''}`}>
                                        {isScanning ? t("Scan your badge now") : t("Assign a badge")}
                                    </button>
                                    :
                                    badgenumber == "" ?
                                        isScanning ?
                                            <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${classes.scanning}`}>
                                                {t("Scan your badge now")}
                                            </button>
                                            :
                                            <button
                                                onClick={() => handleScanBarcode()}
                                                className={classes.form__editBadgeButton}
                                                onMouseEnter={() => setHovered(true)}
                                                onMouseLeave={() => setHovered(false)}
                                            >
                                                {hovered ? t("Scan new badge") : t("This user has a badge")}
                                            </button>
                                        :
                                        <button
                                            onClick={() => handleScanBarcode()}
                                            className={classes.form__badgeButton}
                                            onMouseEnter={() => setHovered(true)}
                                            onMouseLeave={() => setHovered(false)}
                                        >
                                            {hovered ? t("Scan again") : badgenumber}
                                        </button>
                                :
                                badgenumber == "" ?
                                    <button onClick={() => handleScanBarcode()} className={`${classes.form__noBadgeButton} ${isScanning ? classes.scanning : ''}`}>
                                        {isScanning ? t("Scan your badge now") : t("Assign a badge")}
                                    </button>
                                    :
                                    <button
                                        onClick={() => handleScanBarcode()}
                                        className={classes.form__badgeButton}
                                        onMouseEnter={() => setHovered(true)}
                                        onMouseLeave={() => setHovered(false)}
                                    >
                                        {hovered ? 'Scan again' : badgenumber}
                                    </button>
                            }
                            <input type="text" tabIndex={"-1"} style={{ opacity: "0", height: "0px", margin: "0px", padding: "0px", border: "none", position: "absolute" }} onBlur={() => setIsScanning(false)} autoComplete='off' onKeyDown={(e) => processBarcode(e)} name="barcode" id="barcode" ref={inputRef} />
                        </div>
                        <div className={classes.form__row}>
                            <div className={classes.form__inputContainer} style={{ width: "40%" }}>
                                <label className={classes.form__label} style={{ color: idError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{idError ? idError : t("Id")}</label>
                                <input className={classes.form__input} style={{ borderColor: idError ? "#D84949" : "#A9A9A9" }} type="text" placeholder={t("Your initials")} value={id} onChange={(e) => setId(e.target.value)} />
                            </div>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label} style={{ color: nameError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{nameError ? nameError : t("Name")}</label>
                                <input className={classes.form__input} style={{ borderColor: nameError ? "#D84949" : "#A9A9A9" }} type="text" value={name} onChange={(e) => setName(e.target.value)} />
                            </div>
                        </div>
                        <div className={classes.form__column}>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label} style={{ color: emailError ? "#D84949" : FlagstoneTheme.palette.text.primary }}>{emailError ? emailError : t("Email address")}</label>
                                <input className={classes.form__input} style={{ borderColor: emailError ? "#D84949" : "#A9A9A9" }} type="text" value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                        </div>
                        <div className={classes.form__row}>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label}>{t("Role")}</label>
                                <div className={classes.form__switchContainer}>
                                    <FormControlLabel
                                        className={classes.form__switchLabel}
                                        control={<FlagstoneSwitch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />}
                                        label={t("Admin")}
                                    />
                                </div>
                            </div>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label}>{t("Receive email")}</label>
                                <div className={classes.form__switchContainer}>
                                    <FormControlLabel
                                        className={classes.form__switchLabel}
                                        control={<FlagstoneSwitch checked={receiveMail} onChange={(e) => setReceiveMail(e.target.checked)} />}
                                        label={t("Yes")}
                                    />
                                </div>
                            </div>
                            <div className={classes.form__inputContainer}>
                                <label className={classes.form__label}>{t("Active user")}</label>
                                <div className={classes.form__switchContainer}>
                                    <FormControlLabel
                                        className={classes.form__switchLabel}
                                        control={<FlagstoneSwitch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                                        label={t("Active")}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent >
            <DialogActions className={classes.dialog__buttonContainer}>
                <Button className={classes.dialog__button__secondary} onClick={() => cancel()}>
                    {t('Cancel')}
                </Button>
                <Button className={classes.dialog__button} onClick={() => submitUser()}>
                    {editing ? t('Update user') : t('Create user')}
                </Button>
            </DialogActions>
        </Dialog >
    );
}

const useStyles = makeStyles(() => ({

    dialog: {
        margin: "auto"
    },

    dialogPaper: {
        width: '420px !important',
        height: 'auto',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },

    },

    dialog__contentContainer: {
        padding: '0px 16px !important',
        marginBottom: "24px",

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    dialog__form: {
        width: "100%",

        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__column: {
        width: "100%",
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    form__row: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
    },

    form__badgeContainer: {
        width: '100%',
        margin: "4px 0px",
    },

    form__noBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: '#0C0E29',

        backgroundColor: '#F8F8F8',
        border: 'none',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #EDEDED",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    scanning: {
        backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
        backgroundSize: '200% 100%',
        border: '1px solid #E2F8E0',
        animation: '$moveGradient 2s linear infinite',
        animationTimingFunction: 'ease-in-out',
        animationDelay: '0.5s',

        '&:hover': {
            backgroundImage: 'linear-gradient(90deg, #F3FBF2 25%, #E2F8E0 50%, #F3FBF2 75%)',
            backgroundSize: '200% 100%',
            border: '1px solid #E2F8E0',
            animation: '$moveGradient 2s linear infinite',
            animationTimingFunction: 'ease-in-out',
            animationDelay: '0.5s',
            color: FlagstoneTheme.palette.text.primary,
        },
    },
    '@keyframes moveGradient': {
        '0%': {
            backgroundPosition: '200% 0%',
        },
        '100%': {
            backgroundPosition: '-200% 0%',
        },
    },

    form__badgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.primary.main,

        backgroundColor: '#EDF2FF',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C3D3FF",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__editBadgeButton: {
        width: "100%",
        height: "54px",

        fontSize: '18px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        color: FlagstoneTheme.palette.text.primary,

        backgroundColor: '#F3FBF2',
        cursor: 'pointer',

        boxSizing: 'border-box',
        borderRadius: '6px',

        border: "1px solid #C6EFC3",

        transition: 'all 0.2s',

        '&:hover': {
            backgroundColor: '#4654FB',
            border: '1px solid #4654FB',
            color: '#F8F8F8',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },
    },

    form__inputContainer: {
        width: '100%',

        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    form__switchContainer: {
        width: '100%',
        height: "40px",

        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },

    form__inputFieldContainer: {
        position: 'relative',
    },

    form__switchLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,

        gap: "8px !important",
        margin: "0px !important",
    },

    form__label: {
        fontSize: '12px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: FlagstoneTheme.palette.text.primary,
    },

    form__input: {
        width: '100%',
        height: '40px',
        borderRadius: '3px',
        border: '1px solid #A9A9A9',

        padding: '0px 8px',
        paddingLeft: '8px',
        boxSizing: 'border-box',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif !important',
        fontWeight: 'regular',
        color: '#0C0E29',

        "&:focus": {
            outline: "none",
            border: "1px solid #4654FB",
        },
    },

    dialog__inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    dialog__buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between !important',
        width: "100% !important",
        boxSizing: 'border-box',

        padding: '16px !important',
        paddingTop: '0px !important',
        margin: '0px !important',
    },

    dialog__button__secondary: {
        color: FlagstoneTheme.palette.primary.main + " !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        textTransform: "none !important",
        backgroundColor: "white !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    buttons: {
        textAlign: "center"
    },
    stepButton: {
        zIndex: 1,
        color: "#fff",
        width: 50,
        height: 50,
        display: "flex",
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
    },

    dialog__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: "none !important",

        // align right
        marginLeft: 'auto !important',

        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },
}));
